/** §3.1 对局参数。服务端为唯一权威，客户端只读展示。 */

export const PROTOCOL_VERSION = 1 as const;

export const GAME_NAME = "接词大乱斗";
export const GAME_TAGLINE = "成语接龙";

export const INITIAL_HP = 3;
export const HINTS_PER_MATCH = 2;
/** 活字乱序：每局 1 张，打乱对方 26 键，持续对方 2 个行动回合。 */
export const SCRAMBLE_ITEMS_PER_MATCH = 1;
export const SCRAMBLE_TURNS = 2;
export const BOT_SCRAMBLE_EXTRA_MS = 3_000;
export const ITEM_SCRAMBLE = "SCRAMBLE" as const;
export const TURN_TIMEOUT_INIT_MS = 20_000;
export const TURN_TIMEOUT_MIN_MS = 20_000;
export const TURN_TIMEOUT_DECAY_MS = 0;
export const TURN_TIMEOUT_DECAY_EVERY = 5;
export const NETWORK_GRACE_MS = 400;
/** 杀进程冷启动再进小游戏通常超过 30s，过短会直接判负导致无法恢复。倒计时期间仍会正常扣血。 */
export const RECONNECT_GRACE_MS = 180_000;
/** 匹配等待投放机器人的下限（毫秒）。每人入队时在 [MIN, MAX] 内抽一次，避免卡在整 12 秒。 */
export const MATCH_BOT_DELAY_MIN_MS = 12_000;
export const MATCH_BOT_DELAY_MAX_MS = 30_000;
export const MATCH_BOT_DELAY_MS = MATCH_BOT_DELAY_MIN_MS;
export const MATCH_TIMEOUT_MS = 60_000;

export function sampleMatchBotDelayMs(rng: () => number): number {
  const span = MATCH_BOT_DELAY_MAX_MS - MATCH_BOT_DELAY_MIN_MS;
  return MATCH_BOT_DELAY_MIN_MS + Math.floor(rng() * (span + 1));
}
export const ROOM_MAX_ROUNDS = 200;

/** 抢答接龙：3 或 4 人房，整局 4 分钟按分排名。 */
export const RUSH_MIN_SEATS = 3;
export const RUSH_MAX_SEATS = 4;
export const RUSH_MATCH_MS = 240_000;
export const RUSH_ROUND_MS = 12_000;
export const RUSH_COUNTDOWN_MS = 3_000;
/** 公共抢答房人满后，未准备玩家的催促时限。 */
export const RUSH_READY_GRACE_MS = 10_000;
/** 连续两次未准备被踢后，禁止再进抢答的时长。 */
export const RUSH_AFK_BAN_MS = 300_000;
export const RUSH_AFK_STRIKES = 2;

export function clampRushSeats(n: unknown): 3 | 4 {
  return Number(n) === 3 ? 3 : 4;
}

export const ROOM_CREATED_READY_MS = 5_000;
export const ROOM_ENDED_TTL_MS = 60_000;
/** 私房结束后保留房号、供原桌再来的时长。 */
export const ROOM_REMATCH_TTL_MS = 300_000;
export const GRACEFUL_SHUTDOWN_WAIT_MS = 180_000;

export const CLIENT_HEARTBEAT_MS = 15_000;
export const WS_PING_INTERVAL_MS = 20_000;
export const WS_PONG_TIMEOUT_MS = 40_000;

export const MAX_MESSAGES_PER_SECOND = 10;
export const MAX_CANDIDATE_QUERIES_PER_ROUND = 8;
export const MAX_WORD_CHARS = 8;
export const MIN_ABBR_SYLLABLES = 4;
/** 接龙码字只收四字声母，每个字一个字母。 */
export const MAX_ABBR_INITIALS = 4;
export const MAX_CANDIDATES = 5;
export const HUMAN_REACTION_FLOOR_MS = 200;

export const RATE_LIMIT_DISCONNECT_THRESHOLD = 10;

export const OPENING_MIN_BRANCH = 20;
export const BOT_TAIL_MIN_BRANCH = 10;

export const BOT_RESPONSE_MEAN_MS = 4_500;
export const BOT_RESPONSE_STD_MS = 1_500;
export const BOT_RESPONSE_MIN_MS = 1_800;
export const BOT_RESPONSE_MAX_RATIO = 0.8;

export const BOT_MISTAKE_RATE = {
  novice: 0.25,
  normal: 0.15,
  expert: 0.08,
} as const;

export type BotDifficulty = keyof typeof BOT_MISTAKE_RATE;

export const NEW_USER_FORCE_NOVICE_GAMES = 3;
export const LOSE_STREAK_DOWNGRADE = 2;

export const ERROR_CODES = {
  PROTOCOL_MISMATCH: 1001,
  UNAUTHENTICATED: 1002,
  AUTH_FAILED: 1003,
  NOT_YOUR_TURN: 2001,
  ROOM_GONE: 2002,
  DUPLICATE: 2003,
  RECONNECT_FAILED: 2004,
  ROOM_CODE_INVALID: 2005,
  HINTS_EXHAUSTED: 2006,
  HINT_UNAVAILABLE: 2007,
  ITEM_EXHAUSTED: 2011,
  ITEM_UNAVAILABLE: 2012,
  ROOM_STARTED: 2008,
  RUSH_AFK_KICK: 2009,
  RUSH_BANNED: 2010,
  NICKNAME_BLOCKED: 3001,
  INTERNAL: 5000,
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

/** 非 1v1 匹配（好友雅间、抢答 3/4 人）每天首次完局，胜者计入的胜场数。 */
export const FRIEND_DAILY_WIN_MULTIPLIER = 2;
export const FRIEND_DAILY_WIN_COPY = "每天首次与好友完成对局，胜场翻倍";
/** 按北京时间自然日判定「每天首次」。中国无夏令时。 */
export const FRIEND_DAY_TZ_OFFSET_MS = 8 * 60 * 60 * 1000;

export function friendDayKey(nowMs: number): string {
  return new Date(nowMs + FRIEND_DAY_TZ_OFFSET_MS).toISOString().slice(0, 10);
}

export const FUZZY_PINYIN_LEVEL = 0 as const;

export type FuzzyPinyinLevel = 0 | 1;

export function turnTimeoutMs(_round: number): number {
  return TURN_TIMEOUT_INIT_MS;
}

export function punishLevelFromHp(hp: number): number {
  return INITIAL_HP - hp;
}

export function isSubmitInTime(now: number, deadline: number): boolean {
  return now <= deadline + NETWORK_GRACE_MS;
}
